import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { usePath, navigate, parseRoute } from '../../lib/router';
import Modal from '../shared/Modal';
import { Plus, X, Edit3, Trash2, RefreshCw, Building, Search, Link as LinkIcon, Mail, CheckCircle, Briefcase, Eye, Users, MapPin, FileText, UserCheck, UserX, Ban, ArrowRight, ArrowUp, ArrowDown, Shield, CheckSquare, Square, Send, Archive, ArchiveRestore, Crown } from 'lucide-react';
import { Employer } from '../../types';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { AdminOrgInviteModal } from './AdminOrgInviteModal';
import OrgDocumentsSection from './OrgDocumentsSection';
import OrgSpecialRequestsSection from './OrgSpecialRequestsSection';
import { ListPagination, useListPagination } from './ListPagination';
import { getActiveWorkspaceId } from '../../lib/workspaces';
import { useWorkspaces } from '../shared/WorkspaceProvider';

interface EmployersManagerProps {
  canEdit?: boolean;
}

export default function EmployersManager({ canEdit = true }: EmployersManagerProps = {}) {
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  useBodyScrollLock(showModal || showInviteModal);
  const [editing, setEditing] = useState<Employer | null>(null);
  const [form, setForm] = useState({
    companyName: '', contactName: '', contactEmail: '', contactPhone: '',
    website: '', description: '', address: '', country: '', industry: '',
    status: 'active', notes: '',
    relatedPrograms: [] as string[],
  });
  const [linkModal, setLinkModal] = useState<{ id: string; companyName: string; email: string } | null>(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);
  // Company detail modal state
  const [companyDetail, setCompanyDetail] = useState<{ company: any; jobs: any[] } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync companyDetail with URL path.
  //
  // #1125 Item 5: this effect must NOT depend on companyDetail. With the
  // detail in the dep list, the "View jobs" click itself killed the
  // modal: viewCompanyDetail set companyDetail while the path was still
  // '/employers', this effect re-ran on that commit and cleared the
  // detail (else-branch), the navigate effect below bounced the route
  // back — an endless clear/refetch loop in which every fetch SUCCEEDED
  // (hence no error toast) and the modal never stabilised. The sibling
  // ReferrersManager escapes because its sync effect never reads its
  // detail state. Compare ids through a ref instead: the effect is
  // purely route-driven.
  const companyDetailIdRef = useRef<string | null>(null);
  useEffect(() => {
    companyDetailIdRef.current = companyDetail?.company?.id ?? null;
  }, [companyDetail]);

  useEffect(() => {
    if (currentRoute.type === 'employers_detail') {
      const targetId = currentRoute.id;
      if (companyDetailIdRef.current !== targetId) {
        setLoadingDetail(true);
        safeFetch(`/api/admin/companies/${targetId}`)
          .then((data) => {
            setCompanyDetail(data);
          })
          .catch((err: any) => showToast(err.message || 'Error loading details', 'error'))
          .finally(() => setLoadingDetail(false));
      }
    } else if (currentPath === '/employers') {
      if (companyDetailIdRef.current) {
        setCompanyDetail(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const prevCompanyDetailRef = useRef(companyDetail);

  // Sync URL path when companyDetail changes
  useEffect(() => {
    if (companyDetail?.company?.id) {
      navigate(`/employers/adminview/${companyDetail.company.id}`);
    } else if (prevCompanyDetailRef.current && !companyDetail && currentRoute.type === 'employers_detail') {
      navigate('/employers');
    }
    prevCompanyDetailRef.current = companyDetail;
  }, [companyDetail, currentRoute.type]);
  // User management state
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [promoteModal, setPromoteModal] = useState<{ show: boolean; uid?: string; email?: string }>({ show: false });
  const [promoteEmployerId, setPromoteEmployerId] = useState('');
  const [promoteEmployerSearch, setPromoteEmployerSearch] = useState('');
  const [promoteEmployers, setPromoteEmployers] = useState<{ id: string; companyName: string }[]>([]);
  const [showPromoteDropdown, setShowPromoteDropdown] = useState(false);
  const [programOptions, setProgramOptions] = useState<{ id: string; name: string }[]>([]);

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  // Follow-up Item 12: gate the initial fetch on workspace resolution.
  // workspaceHeader() reads localStorage at call time; fetching before
  // WorkspaceProvider validated the stored id reads the WRONG workspace
  // context and the list sticks at stale zeros until a manual Refresh.
  const { loading: workspaceLoading, activeWorkspaceId } = useWorkspaces();
  const fetchEmployers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/employers');
      setEmployers(data.employers || []);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { if (!workspaceLoading) fetchEmployers(); }, [fetchEmployers, workspaceLoading]);

  // Fetch available programs for the related programs multi-select
  useEffect(() => {
    safeFetch('/api/admin/programs')
      .then((d: any) => setProgramOptions(d.programs || []))
      .catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ companyName: '', contactName: '', contactEmail: '', contactPhone: '',
      website: '', description: '', address: '', country: '', industry: '',
      status: 'active', notes: '',
      relatedPrograms: [] });
    setEditing(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, relatedPrograms: form.relatedPrograms || [] };
      if (editing) {
        await safeFetch(`/api/admin/employers/${editing.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        showToast('Employer updated', 'success');
      } else {
        await safeFetch('/api/admin/employers', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        showToast('Employer created', 'success');
      }
      setShowModal(false); resetForm(); fetchEmployers();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog('Delete this employer? This cannot be undone.'))) return;
    try {
      await safeFetch(`/api/admin/employers/${id}`, { method: 'DELETE' });
      showToast('Employer deleted', 'success');
      fetchEmployers();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleEdit = (emp: Employer) => {
    setForm({
      companyName: emp.companyName, contactName: emp.contactName, contactEmail: emp.contactEmail,
      contactPhone: emp.contactPhone || '', website: emp.website || '', description: emp.description || '',
      address: emp.address || '', country: emp.country || '', industry: emp.industry || '',
      status: emp.status || 'active', notes: (emp as any).notes || '',
      relatedPrograms: (emp as any).relatedPrograms || [],
    });
    setEditing(emp);
    setShowModal(true);
  };

  // ── User Management Actions (block/unblock/remove) ────────────────
  const handleUserAction = async (employerId: string, uid: string, action: string, userLabel?: string) => {
    const who = userLabel || 'this user';
    const copy: Record<string, { message: string; confirmLabel: string }> = {
      'promote-agent': { message: `Promote ${who} to Super Agent? This takes effect immediately.`, confirmLabel: 'Promote' },
      'demote-agent': { message: `Demote ${who} to a regular agent? This takes effect immediately.`, confirmLabel: 'Demote' },
      'block': { message: `Block ${who}? They will lose access to this employer's portal until they are unblocked.`, confirmLabel: 'Block' },
      'unblock': { message: `Unblock ${who}? They will regain access to this employer's portal.`, confirmLabel: 'Unblock' },
      'remove': { message: `Remove ${who} from this employer? They will lose access to the employer's portal.`, confirmLabel: 'Remove' },
    };
    const c = copy[action] || { message: `Apply "${action}" to ${who}?`, confirmLabel: 'Confirm' };
    if (!(await confirmDialog(c.message, { confirmLabel: c.confirmLabel }))) return;
    setUserActionLoading(true);
    try {
      const data = await safeFetch(`/api/admin/employers/${employerId}/user-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, action }),
      });
      showToast(`User ${action}ed successfully`, 'success');
      fetchEmployers();
      if (companyDetail) viewCompanyDetail({ id: employerId } as Employer);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setUserActionLoading(false); }
  };

  // ── Formally change the employer's Super Agent (admin-only path) ──
  // The designated Super Agent cannot be removed/demoted/blocked (the
  // server refuses); this is the sanctioned way the designation moves.
  const handleChangeSuper = async (employerId: string, uid: string, email: string) => {
    if (!(await confirmDialog(`Make ${email} the Super Agent for this employer?\n\nThe current Super Agent becomes a regular agent and loses Super Agent protection (they can then be removed, demoted, or deactivated normally). ${email} gains that protection. This is the only way the Super Agent designation changes.`))) return;
    setUserActionLoading(true);
    try {
      await safeFetch(`/api/admin/employers/${employerId}/change-super`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      showToast('Super Agent changed', 'success');
      fetchEmployers();
      if (companyDetail) viewCompanyDetail({ id: employerId } as Employer);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setUserActionLoading(false); }
  };

  // ── Open promotion modal for a user ─────────────────────────────────
  const openPromoteModal = async (uid: string, email: string) => {
    setPromoteModal({ show: true, uid, email });
    setPromoteEmployerId('');
    setPromoteEmployerSearch('');
    try {
      const data = await safeFetch('/api/admin/employers');
      const list = (data.employers || []).map((e: any) => ({ id: e.id, companyName: e.companyName }));
      setPromoteEmployers(list);
    } catch {}
  };

  // ── Execute promotion ──────────────────────────────────────────────
  const executePromotion = async () => {
    if (!promoteModal.uid || !promoteEmployerId) return;
    setUserActionLoading(true);
    try {
      await safeFetch('/api/admin/users/promote-to-employer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: promoteModal.uid, employerId: promoteEmployerId, email: promoteModal.email }),
      });
      showToast('User promoted to employer successfully', 'success');
      setPromoteModal({ show: false });
      fetchEmployers();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setUserActionLoading(false); }
  };

  const viewCompanyDetail = async (emp: Employer) => {
    setLoadingDetail(true);
    try {
      const data = await safeFetch(`/api/admin/companies/${emp.id}`);
      setCompanyDetail(data);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLoadingDetail(false); }
  };

  const handleLinkUser = async () => {
    if (!linkModal || !linkEmail.trim()) return;
    setLinking(true);
    try {
      await safeFetch(`/api/admin/employers/${linkModal.id}/link-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: linkEmail.trim().toLowerCase() }),
      });
      showToast(`User linked to ${linkModal.companyName}`, 'success');
      setLinkModal(null); setLinkEmail('');
      fetchEmployers();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLinking(false); }
  };

  const filtered = employers.filter(e => {
    // Archived orgs are hidden from the main list; the Archived view shows only them.
    // Approved AND rejected employers remain visible until explicitly archived.
    if (!!e.archived !== showArchived) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const idStr = (e.employerId || ('EMP-' + (e.numericId || ''))).toLowerCase();
    return idStr.includes(q) ||
      (e.companyName || '').toLowerCase().includes(q) ||
      (e.contactName || '').toLowerCase().includes(q) ||
      (e.contactEmail || '').toLowerCase().includes(q);
  });

  // Client-side paging over the full filtered set (the endpoint returns
  // every employer — see the PR-17 note on GET /api/admin/employers in
  // server.ts; ListPagination holds the shared pattern). Resets to
  // page 1 when the search text or Active/Archived view changes.
  const pager = useListPagination(filtered, `${searchQuery}|${showArchived}`);

  // Archive / unarchive: archived employers are hidden from the main list but stay
  // fully accessible via the Archived view.
  const handleArchiveToggle = async (id: string, archive: boolean) => {
    const label = employers.find(e => e.id === id)?.companyName || 'this employer';
    if (!(await confirmDialog(archive
      ? `Archive ${label}? It will be hidden from the active employer list. You can restore it from the archive at any time.`
      : `Restore ${label} from the archive? It will return to the active employer list.`))) return;
    try {
      await safeFetch(`/api/admin/employers/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          archived: archive,
          archivedAt: archive ? new Date().toISOString() : null,
        }),
      });
      setEmployers(prev => prev.map(e => (e.id === id ? { ...e, archived: archive, archivedAt: archive ? new Date().toISOString() : null } : e)));
      showToast(archive ? 'Employer archived' : 'Employer restored from archive', 'success');
    } catch (err: any) { showToast(err.message || 'Failed to update archive status', 'error'); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 animate-fade-in">
      {/* Toasts render via the global root-level notification layer. */}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building className="h-6 w-6 text-brand" />
          <h1 className="text-xl font-black text-slate-900 tracking-tight">Employers</h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <button onClick={() => { resetForm(); setShowModal(true); }}
                className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl  transition-all cursor-pointer flex items-center gap-1.5 shadow-lg shadow-blue-600/20">
                <Plus className="h-3.5 w-3.5" /> Add Employer
              </button>
              <button onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-lg shadow-success/20">
                <Send className="h-3.5 w-3.5" /> Invite Employer
              </button>
            </>
          )}
          <button aria-label="Refresh employers" title="Refresh employers" onClick={fetchEmployers} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-all cursor-pointer">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="relative max-w-md mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search employers..." className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
        </div>
        <div className="flex items-center rounded-xl border border-slate-200 overflow-hidden text-xs font-bold shrink-0">
          <button onClick={() => setShowArchived(false)} className={`px-3 py-2 transition cursor-pointer ${!showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>Active</button>
          <button onClick={() => setShowArchived(true)} className={`px-3 py-2 transition cursor-pointer flex items-center gap-1 ${showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}><Archive className="h-3.5 w-3.5" />Archived ({employers.filter(e => e.archived).length})</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="premium-loader"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Building className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-medium">No employers found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-500 uppercase tracking-wider">
            <span className="col-span-3">Employer</span>
            <span className="col-span-2">Contact</span>
            <span className="col-span-2">Industry / Country</span>
            <span className="col-span-2">Users</span>
            <span className="col-span-1">Status</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          <div className="divide-y divide-slate-50">
            {pager.pageItems.map(emp => {
              const activeUsers = (emp.linkedUsers || []).filter((u: any) => u.status === 'active').length;
              return (
                <div key={emp.id} className="grid grid-cols-12 gap-3 px-5 py-4 items-center hover:bg-slate-50 transition-all">
                  <div className="col-span-3 min-w-0 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0"><Building className="h-5 w-5 text-brand" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-black text-slate-900 truncate">{emp.companyName}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] font-mono text-brand bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded font-bold">
                          {emp.employerId || ((emp as any).numericId ? `EMP-${(emp as any).numericId}` : '—')}
                        </span>
                        <span className="text-[11px] text-slate-500 truncate">{emp.contactName || 'No primary contact'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-xs font-bold text-slate-700 truncate">{emp.contactName}</p>
                    <p className="text-[11px] text-slate-500 truncate">{emp.contactEmail}</p>
                  </div>
                  <div className="col-span-2 flex flex-wrap gap-1">
                    {emp.industry && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded">{emp.industry}</span>}
                    {emp.country && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded">{emp.country}</span>}
                  </div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded"><Users className="h-3 w-3" />{activeUsers} active</span>
                  </div>
                  <div className="col-span-1">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-black ${emp.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{emp.status || 'pending'}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    <button onClick={() => viewCompanyDetail(emp)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-500 transition-all cursor-pointer" title="View jobs"><Briefcase className="h-3.5 w-3.5" /></button>
                    {emp.userId ? <span className="p-1.5 text-emerald-600" title="Linked"><CheckCircle className="h-3.5 w-3.5" /></span> : <button onClick={() => { setLinkModal({ id: emp.id, companyName: emp.companyName, email: emp.contactEmail }); setLinkEmail(emp.contactEmail); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-brand transition-all cursor-pointer" title="Link user"><LinkIcon className="h-3.5 w-3.5" /></button>}
                    <button onClick={() => handleEdit(emp)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-all cursor-pointer" title="Edit"><Edit3 className="h-3.5 w-3.5" /></button>
                    {emp.archived ? (
                      <button onClick={() => handleArchiveToggle(emp.id, false)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-all cursor-pointer" title="Restore from archive"><ArchiveRestore className="h-3.5 w-3.5" /></button>
                    ) : (
                      <button onClick={() => handleArchiveToggle(emp.id, true)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-all cursor-pointer" title="Archive"><Archive className="h-3.5 w-3.5" /></button>
                    )}
                    <button onClick={() => handleDelete(emp.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-300 transition-all cursor-pointer" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ListPagination
          page={pager.page}
          pageCount={pager.pageCount}
          total={pager.total}
          start={pager.start}
          end={pager.end}
          pageSize={pager.pageSize}
          onPageChange={pager.setPage}
          onPageSizeChange={pager.setPageSize}
          itemLabel="employers"
        />
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal onClose={() => { setShowModal(false); resetForm(); }} ariaLabel={editing ? 'Edit Employer' : 'Add Employer'}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-base font-black text-slate-900">{editing ? 'Edit Employer' : 'Add Employer'}</h2>
              <button aria-label="Close" title="Close" onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Company Name *</label>
                  <input type="text" required value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Contact Name *</label>
                  <input type="text" required value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Contact Email *</label>
                  <input type="email" required value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Phone</label>
                  <input type="text" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Website</label>
                  <input type="text" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Industry</label>
                  <input type="text" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Country</label>
                  <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Address</label>
                  <input type="text" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Related Programs</label>
                  <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-xl bg-white">
                    {programOptions.length === 0 && (
                      <p className="text-[11px] text-slate-500 py-2">Loading programs...</p>
                    )}
                    {programOptions.map((prog) => {
                      const pName = prog.name || prog.id;
                      const selected = (form.relatedPrograms || []).includes(pName);
                      return (
                        <button
                          key={prog.id}
                          type="button"
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              relatedPrograms: selected
                                ? (f.relatedPrograms || []).filter((p: string) => p !== pName)
                                : [...(f.relatedPrograms || []), pName],
                            }));
                          }}
                          className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-lg border transition cursor-pointer ${
                            selected
                              ? 'bg-brand-active border-blue-200'
                              : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {selected ? <CheckSquare className="h-3 w-3" /> : <Square className="h-3 w-3" />}
                          {pName}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Employers will only see talent pool applicants from the selected programs.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Account Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 font-bold bg-white"
                  >
                    <option value="active">Active (Approved)</option>
                    <option value="pending">Pending Approval</option>
                    <option value="suspended">Suspended</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Internal Admin Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Internal staff notes regarding this employer..."
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                  <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 btn-primary text-white text-xs font-bold rounded-xl  transition-all cursor-pointer shadow-lg shadow-blue-600/20">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Link User Modal */}
      {linkModal && (
        <Modal onClose={() => { setLinkModal(null); }} ariaLabel="Link User to Employer">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900">Link User to Employer</h2>
              <button aria-label="Close" title="Close" onClick={() => setLinkModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 rounded-xl px-4 py-3">
                <p className="text-xs text-amber-700 font-medium">
                  Link a user account to <strong>{linkModal.companyName}</strong>. The user will log in with this email and access
                  the employer dashboard.
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Employer Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input type="email" required value={linkEmail} onChange={e => setLinkEmail(e.target.value)}
                    placeholder="employer@company.com"
                    className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">A Firebase Auth account will be created if one doesn't exist.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setLinkModal(null)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button type="button" onClick={handleLinkUser} disabled={linking || !linkEmail.trim()}
                  className="px-6 py-2 btn-primary text-white text-xs font-bold rounded-xl  disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-blue-600/20">
                  {linking ? 'Linking...' : 'Link User'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Company Detail Modal */}
      {companyDetail && (
        <Modal onClose={() => setCompanyDetail(null)} ariaLabel="Company Profile">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-black text-slate-900">{companyDetail.company?.companyName}</h2>
                <p className="text-xs text-slate-500">Company Profile &amp; Jobs</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setCompanyDetail(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              {/* Company info */}
              <div className="grid grid-cols-3 gap-4 mb-6 bg-slate-50 rounded-xl p-4">
                {companyDetail.company?.contactName && (
                  <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Contact</label><p className="text-xs font-medium text-slate-700">{companyDetail.company.contactName}</p></div>
                )}
                {companyDetail.company?.contactEmail && (
                  <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Email</label><p className="text-xs text-slate-600">{companyDetail.company.contactEmail}</p></div>
                )}
                {companyDetail.company?.country && (
                  <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Country</label><p className="text-xs text-slate-600">{companyDetail.company.country}</p></div>
                )}
                {companyDetail.company?.industry && (
                  <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Industry</label><p className="text-xs text-slate-600">{companyDetail.company.industry}</p></div>
                )}
              </div>

              {/* Organization documents (Slice 1) */}
              <div className="mb-4">
                <OrgDocumentsSection orgType="employer" orgId={companyDetail.company.id} canEdit={canEdit} />
              </div>

              {/* Organization special requests (Slice 2) */}
              <div className="mb-4">
                <OrgSpecialRequestsSection orgType="employer" orgId={companyDetail.company.id} canEdit={canEdit} />
              </div>

              {/* Linked Users */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-indigo-600" />
                    Linked Users ({(companyDetail.company?.linkedUsers || []).length})
                  </h3>
                  <button
                    onClick={() => { setLinkModal({ id: companyDetail.company.id, companyName: companyDetail.company.companyName, email: companyDetail.company.contactEmail || '' }); setLinkEmail(''); }}
                    className="px-2.5 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add User
                  </button>
                </div>
                {(!companyDetail.company?.linkedUsers || companyDetail.company.linkedUsers.length === 0) ? (
                  <p className="text-xs text-slate-500 text-center py-3">No users linked to this employer</p>
                ) : (
                  <div className="space-y-2">
                    {companyDetail.company.linkedUsers.filter((u: any) => u.status !== 'removed').map((u: any) => (
                      <div key={u.uid} className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <Mail className="h-3.5 w-3.5 text-indigo-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{u.email}</p>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${
                                u.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                u.status === 'blocked' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-500'
                              }`}>{u.status}</span>
                              <span className={`px-1.5 py-0.5 rounded font-medium ${u.agentRole === 'super' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {u.agentRole === 'super' ? 'Super Agent' : 'Regular Agent'}
                              </span>
                              {companyDetail.company?.userId && u.uid === companyDetail.company.userId && (
                                <span className="px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700" title="Designated Super Agent — protected from removal/demotion; use Make Super Agent on another member to change">Owner</span>
                              )}
                              {u.invitedAt && <span>Since {new Date(u.invitedAt).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {u.status === 'active' && u.agentRole === 'regular' && (
                            <button onClick={() => handleUserAction(companyDetail.company.id, u.uid, 'promote-agent', u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Promote to Super Agent">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.agentRole === 'super' && u.uid !== companyDetail.company?.userId && companyDetail.company.linkedUsers.filter((lu: any) => lu.agentRole === 'super' && lu.status !== 'removed').length > 1 && (
                            <button onClick={() => handleUserAction(companyDetail.company.id, u.uid, 'demote-agent', u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer" title="Demote to Regular Agent">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== companyDetail.company?.userId && (
                            <button onClick={() => handleChangeSuper(companyDetail.company.id, u.uid, u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition cursor-pointer" title="Make Super Agent (formal change — the current Super Agent becomes a regular agent)">
                              <Crown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== companyDetail.company?.userId && (
                            <button onClick={() => handleUserAction(companyDetail.company.id, u.uid, 'block', u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Block">
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'blocked' && (
                            <button onClick={() => handleUserAction(companyDetail.company.id, u.uid, 'unblock', u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition cursor-pointer" title="Unblock">
                              <UserCheck className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.uid !== companyDetail.company?.userId && (
                            <button onClick={() => handleUserAction(companyDetail.company.id, u.uid, 'remove', u.email)} disabled={userActionLoading}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition cursor-pointer" title="Remove">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.uid !== companyDetail.company?.userId && (
                            <button onClick={() => openPromoteModal(u.uid, u.email)}
                              className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition cursor-pointer" title="Reassign to another company">
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Jobs list */}
              <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-indigo-600" />
                Jobs ({companyDetail.jobs?.length || 0})
              </h3>
              {loadingDetail ? (
                <div className="py-10 text-center text-xs text-slate-500">Loading jobs...</div>
              ) : companyDetail.jobs?.length === 0 ? (
                <div className="py-10 text-center">
                  <Briefcase className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No jobs for this company yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {companyDetail.jobs.map((job: any) => (
                    <div key={job.id} className="border border-slate-200 rounded-xl p-3 hover:shadow-sm transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-bold text-slate-800">{job.title}</h4>
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                              job.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                              job.status === 'published' ? 'bg-blue-100 text-brand' :
                              job.status === 'filled' ? 'bg-teal-100 text-teal-700' :
                              job.status === 'closed' || job.status === 'archived' ? 'bg-slate-100 text-slate-500' :
                              'bg-amber-100 text-amber-700'
                            }`}>{job.status}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
                            <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{job.jobType}</span>
                            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{job.applicantCount || 0} applicant{(job.applicantCount || 0) !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <button onClick={() => setCompanyDetail(null)} className="ml-3 px-2.5 py-1.5 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer shrink-0">
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Promotion / Reassign Modal */}
      {promoteModal.show && (
        <Modal onClose={() => setPromoteModal({ show: false })} ariaLabel={promoteModal.uid ? 'Reassign User' : 'Promote User to Employer'}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Shield className="h-4 w-4 text-indigo-600" />
                {promoteModal.uid ? 'Reassign User' : 'Promote User to Employer'}
              </h2>
              <button aria-label="Close" title="Close" onClick={() => setPromoteModal({ show: false })} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xs text-amber-700 font-medium">
                  {promoteModal.uid
                    ? <>Reassign <strong>{promoteModal.email}</strong> to a different company.</>
                    : <>Promoting <strong>{promoteModal.email}</strong> will migrate their role from Applicant to Employer. Applicant features will be disabled permanently.</>
                  }
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Select Target Company</label>
                <div className="relative">
                  <input type="text" required placeholder="Search companies..." value={promoteEmployerSearch}
                    onFocus={() => setShowPromoteDropdown(true)}
                    onChange={e => { setPromoteEmployerSearch(e.target.value); setPromoteEmployerId(''); setShowPromoteDropdown(true); }}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                  {showPromoteDropdown && (
                    <div className="absolute z-[8500] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-36 overflow-y-auto">
                      {promoteEmployers.filter(e => e.companyName.toLowerCase().includes(promoteEmployerSearch.toLowerCase())).map(e => (
                        <button key={e.id} type="button" onClick={() => { setPromoteEmployerId(e.id); setPromoteEmployerSearch(e.companyName); setShowPromoteDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-xs font-medium hover:bg-indigo-50 text-slate-700 transition-colors cursor-pointer flex items-center gap-2">
                          <Building className="h-3 w-3 text-slate-400" />{e.companyName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setPromoteModal({ show: false })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button onClick={executePromotion} disabled={!promoteEmployerId || userActionLoading}
                  className="px-6 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer shadow-lg shadow-brand-primary/20 flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5" />{userActionLoading ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Invite Employer Modal */}
      <AdminOrgInviteModal
        isOpen={showInviteModal}
        role="employer"
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          notifyToast('success', 'Invitation sent successfully!');
          fetchEmployers();
        }}
        workspaceId={getActiveWorkspaceId() || ''}
        canEdit={canEdit}
      />
    </div>
  );
}
